import { useState } from "react";
import { TextInput } from "@mantine/core";
import { callFunction } from "../api/client";
import type { Campaign } from "../api/types";
import { useScheduleSetting } from "../lib/campaign";
import { suggestedEnrichOn, todayIso } from "../lib/schedule";
import { Dialog } from "../ui/Dialog";

/* A campaign's two dates, from Set dates or Change dates on its page.
 *
 * Start calling on is the day the team starts calling. Start enrichment on is
 * the day EVIE starts the campaign's enrichment, asked only while the
 * campaign is in Planning with enrichment chosen, because past Planning its
 * enrichment has started or been skipped. A calling date suggests the
 * enrichment date the engine's lead days before it, until the person sets
 * it. The engine checks every date and refuses with a sentence
 * (api.schedule_campaign, engine migration 256). The engine's plan is
 * docs/plans/campaign-scheduling.md. */
export function DatesDialog({ token, c, opened, onClose, onDone }: {
  token: string;
  c: Campaign;
  opened: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const planning = c.stage === "draft";
  const asksEnrichment = planning && Boolean(c.auto_enrich || c.ask_enricher || c.ask_apollo);
  const lead = useScheduleSetting(token);
  const today = todayIso();
  const [callOn, setCallOn] = useState(c.call_on ?? "");
  const [enrichOn, setEnrichOn] = useState(c.enrich_on ?? "");
  const [touched, setTouched] = useState(Boolean(c.enrich_on));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A refusal is about the dates it was given, so a changed date clears it.
  const pickCallOn = (value: string) => {
    setError(null);
    setCallOn(value);
    if (asksEnrichment && !touched && value && lead.data !== undefined) {
      setEnrichOn(suggestedEnrichOn(value, lead.data, today));
    }
  };
  // An enrichment date that has come starts enrichment when it is saved.
  const startsNow = asksEnrichment && enrichOn !== "" && enrichOn <= today;
  const changed = callOn !== (c.call_on ?? "") || (asksEnrichment && enrichOn !== (c.enrich_on ?? ""));

  const save = async () => {
    setBusy(true); setError(null);
    try {
      await callFunction(token, "schedule_campaign", {
        p_name: c.name ?? "",
        // Past Planning the enrichment date is kept as it is.
        p_enrich_on: asksEnrichment ? (enrichOn || null) : (c.enrich_on ?? null),
        p_call_on: callOn || null,
      });
      onDone();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog opened={opened} title={c.call_on || c.enrich_on ? `Change the dates for ${c.label}` : `Set dates for ${c.label}`}
            onClose={onClose} busy={busy} disabled={!changed && !startsNow}
            action={{ label: startsNow ? "Save and start enrichment" : "Save", onPress: save }}
            error={error ?? (lead.isError ? (lead.error as Error).message : null)}>
      <TextInput type="date" label="Estimated starting date"
                 description={(c.sequence_length ?? 1) > 1 && c.follows_name
                   ? "Set to the next working day when the campaigns ahead of it are done."
                   : "The day the team expects to start calling. Empty for no date."}
                 min={today} value={callOn} onChange={(e) => pickCallOn(e.currentTarget.value)} data-autofocus />
      {asksEnrichment ? (
        <TextInput mt="md" type="date" label="Start enrichment on"
                   description="EVIE starts enrichment on this date. Empty to start it yourself."
                   min={today} max={callOn || undefined} value={enrichOn}
                   onChange={(e) => { setError(null); setTouched(true); setEnrichOn(e.currentTarget.value); }} />
      ) : planning ? (
        <p className="muted">No enrichment is chosen. Start enrichment chooses it.</p>
      ) : null}
    </Dialog>
  );
}
